import type { FC } from 'react';
import type React from 'react';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { useXingUserFeatures } from '@xing-com/crate-hooks-use-xing-user-features';
import { Dialog } from '@xing-com/dialog';
import { Grid, Row, GridContainer } from '@xing-com/xing-grid';

import { useDeviceInfo } from '../../hooks/use-device-info/use-device-info';
import { ROUTES } from '../../routes';
import { trackingParams } from '../../utils/tracking';
import type { LinkBarNavigationProps } from '../link-bar';
import LinkBar from '../link-bar';
import { LoggedInHeader } from './logged-in-header';
import * as Styled from './logged-in-page.styles';

type LoggedInContentProps = {
  children: React.ReactNode;
};
export const LoggedInContent: FC<LoggedInContentProps> = ({ children }) => {
  const { formatMessage } = useIntl();
  const { search, pathname } = useLocation();
  const { isBrandManagerEditor } = useXingUserFeatures();
  const { isWebview } = useDeviceInfo();

  const isRedirectedFromOnlyfy =
    new URLSearchParams(search).get('onlyfyLogin') === 'true';

  const [showRedirectDialog, setShowRedirectDialog] = useState<boolean>(false);

  useEffect(() => {
    setShowRedirectDialog(isRedirectedFromOnlyfy);
  }, [isRedirectedFromOnlyfy]);

  const hasToShowLinkBar = !(
    pathname.includes(ROUTES.industry) ||
    pathname.includes(ROUTES.industries) ||
    pathname.includes(ROUTES.mostVisitedCompanies) ||
    pathname.includes(ROUTES.recentlyClaimedCompanies)
  );

  const navigation: Array<LinkBarNavigationProps> = [
    {
      to: ROUTES.companies,
      trackingAttributes: trackingParams.navigationRecommended,
      copy: formatMessage({ id: 'CP_LANDING_PAGE_RECOMMENDED_LINK' }),
    },
    {
      to: ROUTES.managedCompanies,
      trackingAttributes: trackingParams.navigationManaging,
      copy: formatMessage({ id: 'CP_100_MY_EDITED_COMPANIES_TITLE' }),
    },
    {
      to: ROUTES.followedCompanies,
      trackingAttributes: trackingParams.navigationFollowing,
      copy: formatMessage({ id: 'CP_FOLLOWING_COMPANIES' }),
    },
  ];

  return (
    <>
      <Dialog
        confirmLabel={formatMessage({
          id: 'CP_REDIRECT_PERMISSIONS_DIALOG_BUTTON',
        })}
        onConfirm={() => setShowRedirectDialog(false)}
        headline={formatMessage({
          id: 'CP_REDIRECT_PERMISSIONS_DIALOG_TITLE',
        })}
        text={formatMessage({
          id: 'CP_REDIRECT_PERMISSIONS_DIALOG_MESSAGE',
        })}
        show={showRedirectDialog}
      />
      <GridContainer type="standard">
        <Grid>
          {!isWebview && (
            <LoggedInHeader
              showBackToCompanies={!hasToShowLinkBar}
              isBrandManagerEditor={isBrandManagerEditor as boolean}
            />
          )}
          <Styled.MobileBanner
            dangerouslySetInnerHTML={{
              __html: formatMessage({
                id: 'CP_LANDING_MOBILE_BANNER',
              }),
            }}
          />

          {hasToShowLinkBar && (
            <>
              <Row>
                <Styled.Headline size="xxlarge" as="h1">
                  <FormattedMessage id="SE_LABEL_COMPANIES" />
                </Styled.Headline>
              </Row>
              <Row>
                <LinkBar navigation={navigation} />
              </Row>
            </>
          )}
          <Row>{children}</Row>
        </Grid>
      </GridContainer>
    </>
  );
};
